import React, { useMemo, useCallback } from 'react';
import { preprocessTextForWrapping, getCableDisplayName, getSezioniArray } from '../../utils/cableLogic';
import { extractSiglaturaFromCollegamento } from '../../utils/acPowerLogic';
import { COLOR_MAP, COLORI_MAPPINGS, COLOR_TRANSLATION } from '../../constants/colorConstants';
// import TooltipPortal from '../ui/TooltipPortal'; // Rimosso perché non utilizzato
import ScaleMarking from './ScaleMarking';
import CableMarking from './CableMarking';
import LoopMarking from './LoopMarking';
import LoopScaleMarking from './LoopScaleMarking';
import CableSVGLine from './CableSVGLine'; // Importa CableSVGLine dal suo file

const CablePreview = ({ settings, setSettings, isMobile = false }) => {
  // Cavo fittizio di esempio con lunghezza variabile
  const sampleCable = useMemo(() => ({
    originalIndex: 0,
    siglatura: 'K6.2',
    colore: 'ROSSO',
    sezione: 6,
    lunghezza: settings.lunghezzaPreview || 350,
    terminaleA: 'PSW',
    terminaleB: 'PDR',
    collegamentoA: 'F6.7 (1)',
    collegamentoB: 'MORS. 61 (UP)'
  }), [settings.lunghezzaPreview]);

  const svgWidth = isMobile ? 400 : 600;
  const svgHeight = 140;
  const x1 = isMobile ? 70 : 108;   // CODA (sinistra)
  const x2 = isMobile ? 330 : 492;  // TESTA (destra)
  const y1 = 70; // y2 è uguale a y1 per un cavo orizzontale
  const scaleY = 110; // Posizione Y per la scala millimetrica
  const cableLength = x2 - x1; // Lunghezza del cavo nella vista SVG

  // Calcola posizioni delle marcature - RIBALTATO
  const getMarkingPosition = useCallback((mmPosition, fromEnd = false) => {
    if (fromEnd) {
      // Per le marcature di coda (TAIL), calcola da sinistra
      const ratio = mmPosition / sampleCable.lunghezza;
      return x1 + (cableLength * ratio);
    } else {
      // Per le marcature di testa (HEAD), calcola da destra
      const ratio = mmPosition / sampleCable.lunghezza;
      return x2 - (cableLength * ratio);
    }
  }, [sampleCable.lunghezza, x1, x2, cableLength]);

  // AGGIORNATA: Calcola posizioni loop printing
  const getLoopPositions = useCallback(() => {
    // CORREZIONE: Deve esserci marcatura centrale E loopNum > 0
    if (settings.marcaturaCentrale === 'niente' || settings.loopNum <= 0) {
      return [];
    }

    if (settings.loopMode === 'numero') {
      // Distanza disponibile tra marcature testa e coda
      const distanzaDisponibile = sampleCable.lunghezza - settings.posMarcaturaTesta - settings.posMarcaturaCoda;

      if (distanzaDisponibile <= 0) return [];

      const loopPositions = [];
      for (let i = 1; i <= settings.loopNum; i++) {
        // Formula: (i * distanzaDisponibile / (loopNum + 1)) + posMarcaturaTesta
        const positionFromHead = (i * distanzaDisponibile / (settings.loopNum + 1)) + settings.posMarcaturaTesta;
        loopPositions.push({
          position: positionFromHead,
          content: settings.marcaturaCentrale === 'siglatura' ? sampleCable.siglatura : i.toString(),
          index: i
        });
      }
      return loopPositions;
    } else if (settings.loopMode === 'distanza') {
      // NUOVA LOGICA: Basata sulla distanza
      if (settings.loopDist <= 0) return [];

      const loopPositions = [];
      const posCodaDallaTesta = sampleCable.lunghezza - settings.posMarcaturaCoda;
      let currentPosition = settings.posMarcaturaTesta + settings.loopDist;
      let loopIndex = 1;

      while (currentPosition < posCodaDallaTesta) {
        loopPositions.push({
          position: currentPosition,
          content: settings.marcaturaCentrale === 'siglatura' ? sampleCable.siglatura : loopIndex.toString(),
          index: loopIndex
        });
        currentPosition += settings.loopDist;
        loopIndex++;
      }

      return loopPositions;
    }

    return [];
  }, [settings, sampleCable]);

  const loopPositions = useMemo(() => getLoopPositions(), [getLoopPositions]);

  // Contenuti delle marcature basati sulle impostazioni
  const getMarkingContent = useCallback((type) => {
    // eslint-disable-next-line no-unused-vars
    const language = settings.language || 'it'; // `language` non è usata direttamente qui, ma `settings.lingua` sì. Mantenuta per coerenza se dovesse servire.
    const useAWG = settings.useAWG || false;
    const sezioniArray = getSezioniArray(useAWG);
    const sezioneInfo = sezioniArray.find(s => s.val === sampleCable.sezione);

    switch (type) {
      case 'siglatura':
        return preprocessTextForWrapping(sampleCable.siglatura);
      case 'collegamentoA':
        return preprocessTextForWrapping(sampleCable.collegamentoA);
      case 'collegamentoB':
        return preprocessTextForWrapping(sampleCable.collegamentoB);
      case 'sezione':
        return preprocessTextForWrapping(sezioneInfo ? (useAWG ? `${sezioneInfo.val} AWG` : `${sezioneInfo.val}mm²`) : '');
      case 'lunghezza':
        return preprocessTextForWrapping(`${sampleCable.lunghezza}mm`);
      case 'head1':
        return settings.marcaturePerCollegamento
          ? preprocessTextForWrapping(sampleCable.collegamentoA)
          : preprocessTextForWrapping(sampleCable.siglatura);
      case 'head2':
        if (!settings.abilitaMarcature2) return '';
        return settings.tipoMarcature2 === 'siglatura'
          ? (settings.marcaturePerCollegamento ? preprocessTextForWrapping(sampleCable.collegamentoA) : preprocessTextForWrapping(sampleCable.siglatura))
          : preprocessTextForWrapping(sampleCable.collegamentoA);
      case 'mid':
        if (settings.marcaturaCentrale === 'siglatura') {
          return settings.marcaturePerCollegamento
            ? preprocessTextForWrapping(extractSiglaturaFromCollegamento(sampleCable.collegamentoA))
            : preprocessTextForWrapping(getCableDisplayName(sampleCable, settings.lingua, settings)); // Utilizzo getCableDisplayName qui
        }
        if (settings.marcaturaCentrale === 'numerazione') return '1'; // Questo è solo un esempio, la numerazione dinamica è gestita in loopPositions
        return '';
      case 'tail2':
        if (!settings.abilitaMarcature2) return '';
        return settings.tipoMarcature2 === 'siglatura'
          ? (settings.marcaturePerCollegamento ? preprocessTextForWrapping(sampleCable.collegamentoB) : preprocessTextForWrapping(sampleCable.siglatura))
          : preprocessTextForWrapping(sampleCable.collegamentoB);
      case 'tail1':
        return settings.marcaturePerCollegamento
          ? preprocessTextForWrapping(sampleCable.collegamentoB)
          : preprocessTextForWrapping(sampleCable.siglatura);
      case 'niente':
      default:
        return '';
    }
  }, [settings, sampleCable]); // Rimosso getCableDisplayName come dipendenza in quanto è una funzione pura e non cambia.

  // Queste variabili sono state rimosse o gestite diversamente
  // const showHeadMarking = settings.marcaturaTesta !== 'niente';
  // const showTailMarking = settings.marcaturaCoda !== 'niente';
  // const showCentralMarking = settings.marcaturaCentrale !== 'niente';
  // const markingClass = settings.boldMarcature ? 'font-bold' : '';

  // Funzioni per i colori e formattazione
  const normalizeColorForVisualization = useCallback((color) => {
    if (color === 'YE/GN') return 'GI/VE';
    if (COLOR_TRANSLATION[color]) {
      const translated = COLOR_TRANSLATION[color];
      return COLORI_MAPPINGS.it.includes(translated) ? translated : color; // Corretto: usare COLORI_MAPPINGS
    }
    return color;
  }, []);

  const getCableColor = useCallback((cableColor) => {
    const normalizedColor = normalizeColorForVisualization(cableColor);
    return COLOR_MAP[normalizedColor] || settings.defaultCableColor || '#000000';
  }, [normalizeColorForVisualization, settings.defaultCableColor]);

  return (
    <div className="flex flex-col items-center p-4 bg-gray-50 rounded-lg shadow-inner">
      <h3 className="text-lg font-semibold mb-4">Preview Marcatura Cavo</h3>

      <div className="w-full flex justify-center items-center relative">
        <svg
          className="w-full border border-gray-100 rounded"
          viewBox={`0 0 ${svgWidth} ${svgHeight}`}
          style={{ backgroundColor: '#FAFAFA' }}
        >
          <defs>
            <pattern id="grid" width="20" height="20" patternUnits="userSpaceOnUse">
              <path d="M 20 0 L 0 0 0 20" fill="none" stroke="#E5E7EB" strokeWidth="0.5" opacity="0.2" />
            </pattern>
          </defs>
          <rect width="100%" height="100%" fill="url(#grid)" />

          {/* Cavo principale */}
          <CableSVGLine
            cable={sampleCable}
            x1={x1} y1={y1} x2={x2} y2={y1}
            isHovered={false} // Placeholder, da gestire se necessario
            useAWG={settings.useAWG}
          />

          {/* Etichette TESTA e CODA per chiarezza */}
          <text
            x={x1} y={y1 - 15}
            className="text-xs font-bold fill-red-600"
            textAnchor="middle"
          >
            CODA
          </text>

          <text
            x={x2} y={y1 - 15}
            className="text-xs font-bold fill-green-600"
            textAnchor="middle"
          >
            TESTA
          </text>

          {/* Scala millimetrica */}
          <g className="scale-ruler">
            <line x1={x1} y1={scaleY} x2={x2} y2={scaleY} stroke="#9CA3AF" strokeWidth={2} />

            <line x1={x1} y1={scaleY - 3} x2={x1} y2={scaleY + 3} stroke="#9CA3AF" strokeWidth={1} />
            <line x1={(x1 + x2) / 2} y1={scaleY - 2} x2={(x1 + x2) / 2} y2={scaleY + 2} stroke="#9CA3AF" strokeWidth={1} />
            <line x1={x2} y1={scaleY - 3} x2={x2} y2={scaleY + 3} stroke="#9CA3AF" strokeWidth={1} />

            {/* Etichette scala ribaltate */}
            <text x={x1} y={scaleY - 8} className="text-xs fill-gray-500" textAnchor="start">
              {sampleCable.lunghezza}mm (CODA)
            </text>
            <text x={(x1 + x2) / 2} y={scaleY - 8} className="text-xs fill-gray-500" textAnchor="middle">
              {sampleCable.lunghezza / 2}mm
            </text>
            <text x={x2} y={scaleY - 8} className="text-xs fill-gray-500" textAnchor="end">
              0mm (TESTA)
            </text>
          </g>

          {/* Punti sulla scala */}
          <ScaleMarking
            position={settings.posMarcaturaTesta}
            type="head1"
            fromEnd={false}
            getMarkingPosition={getMarkingPosition}
            scaleY={scaleY}
          />

          {settings.abilitaMarcature2 && (
            <ScaleMarking
              position={settings.posMarcaturaTesta + settings.distanzaMarcature2}
              type="head2"
              fromEnd={false}
              getMarkingPosition={getMarkingPosition}
              scaleY={scaleY}
            />
          )}

          <ScaleMarking
            position={settings.posMarcaturaCoda}
            type="tail1"
            fromEnd={true}
            getMarkingPosition={getMarkingPosition}
            scaleY={scaleY}
          />

          {settings.abilitaMarcature2 && (
            <ScaleMarking
              position={settings.posMarcaturaCoda + settings.distanzaMarcature2}
              type="tail2"
              fromEnd={true}
              getMarkingPosition={getMarkingPosition}
              scaleY={scaleY}
            />
          )}

          {/* Indicatori LOOP sulla scala */}
          {loopPositions.map((loopData, index) => (
            <LoopScaleMarking key={`loop-scale-${index}`} loopData={loopData} getMarkingPosition={getMarkingPosition} scaleY={scaleY} />
          ))}

          {/* Marcature sul cavo */}
          {/* HEAD1 e HEAD2 - dalla testa (destra) */}
          <CableMarking
            type="head1"
            position={settings.posMarcaturaTesta}
            content={getMarkingContent('head1')}
            isFlipped={settings.ribaltaTesta === 1}
            fromEnd={false}
            getMarkingPosition={getMarkingPosition}
            y1={y1}
          />

          {settings.abilitaMarcature2 && (
            <CableMarking
              type="head2"
              position={settings.posMarcaturaTesta + settings.distanzaMarcature2}
              content={getMarkingContent('head2')}
              isFlipped={settings.ribaltaTesta === 1}
              fromEnd={false}
              getMarkingPosition={getMarkingPosition}
              y1={y1}
            />
          )}

          {/* LOOP MARKINGS */}
          {loopPositions.map((loopData, index) => (
            <LoopMarking key={`loop-${index}`} loopData={loopData} getMarkingPosition={getMarkingPosition} y1={y1} settings={settings} />
          ))}

          {/* TAIL1 e TAIL2 - dalla coda (sinistra) */}
          <CableMarking
            type="tail1"
            position={settings.posMarcaturaCoda}
            content={getMarkingContent('tail1')}
            isFlipped={settings.ribaltaCoda === 1}
            fromEnd={true}
            getMarkingPosition={getMarkingPosition}
            y1={y1}
          />

          {settings.abilitaMarcature2 && (
            <CableMarking
              type="tail2"
              position={settings.posMarcaturaCoda + settings.distanzaMarcature2}
              content={getMarkingContent('tail2')}
              isFlipped={settings.ribaltaCoda === 1}
              fromEnd={true}
              getMarkingPosition={getMarkingPosition}
              y1={y1}
            />
          )}

          {/* Terminali */}
          <circle cx={x1} cy={y1} r={8} fill={getCableColor(sampleCable.colore)} className="terminal-circle" />
          <text x={x1 - 10} y={y1 + 5} textAnchor="end" fill="#333" fontSize="10">{sampleCable.terminaleA}</text>

          <circle cx={x2} cy={y1} r={8} fill={getCableColor(sampleCable.colore)} className="terminal-circle" />
          <text x={x2 + 10} y={y1 + 5} textAnchor="start" fill="#333" fontSize="10">{sampleCable.terminaleB}</text>
        </svg>

        {/* Legenda aggiornata */}
        <div className="mt-3 grid grid-cols-4 gap-2 text-xs text-gray-600">
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 bg-red-500 rounded-full"></div>
            <span>Posizioni marcature</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-2 bg-white border border-gray-300 rounded"></div>
            <span>Etichette normali</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-2 bg-yellow-200 border border-yellow-400 rounded"></div>
            <span>Loop printing</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-1 bg-gray-400"></div>
            <span>Scala millimetrica</span>
          </div>
        </div>
        {/* Box calcolo posizioni CON LOOP */}
        {(settings.abilitaMarcature2 || loopPositions.length > 0) && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mt-3">
            <h6 className="text-sm font-medium text-blue-800 mb-2">
              📍 Calcolo Posizioni Marcature:
            </h6>
            <div className="text-xs text-blue-700 space-y-1">
              <div>• <strong>Head1:</strong> {settings.posMarcaturaTesta}mm (dalla testa)</div>
              {settings.abilitaMarcature2 && (
                <div>• <strong>Head2:</strong> {settings.posMarcaturaTesta + settings.distanzaMarcature2}mm (dalla testa)</div>
              )}
              {loopPositions.map((loop, idx) => (
                <div key={idx}>• <strong>Loop {loop.index}:</strong> {Math.round(loop.position)}mm (dalla testa) - Contenuto: "{loop.content}"</div>
              ))}
              <div>• <strong>Tail1:</strong> {settings.posMarcaturaCoda}mm (dalla coda)</div>
              {settings.abilitaMarcature2 && (
                <div>• <strong>Tail2:</strong> {settings.posMarcaturaCoda + settings.distanzaMarcature2}mm (dalla coda)</div>
              )}
              {loopPositions.length > 0 && (
                <div className="mt-2 text-emerald-600">
                  🔄 <em>Loop printing attivo: {settings.loopNum > 0 ? settings.loopNum : 'Auto'} marcature intermedie con modalità "{settings.loopMode}"</em>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Stato flip attivi */}
        {(settings.ribaltaTesta === 1 || settings.ribaltaCoda === 1 || settings.ribaltaCentrale === 1) && (
          <div className="mt-2 text-xs text-amber-600 bg-amber-50 border border-amber-200 rounded px-2 py-1">
            🔄 Flip attivi: {[
              settings.ribaltaTesta === 1 && 'Testa',
              settings.ribaltaCentrale === 1 && 'Centrale',
              settings.ribaltaCoda === 1 && 'Coda'
            ].filter(Boolean).join(', ')}
          </div>
        )}
      </div>

      {/* Controlli per la preview (da implementare se necessario) */}
      <div className="mt-4 w-full max-w-md">
        <label htmlFor="lunghezzaPreview" className="block text-sm font-medium text-gray-700">Lunghezza Cavo Preview (mm): {settings.lunghezzaPreview}</label>
        <input
          type="range"
          id="lunghezzaPreview"
          min="100"
          max="1000"
          step="50"
          value={settings.lunghezzaPreview}
          onChange={(e) => setSettings(prev => ({ ...prev, lunghezzaPreview: parseInt(e.target.value) }))}
          className="mt-1 block w-full"
        />
      </div>
    </div>
  );
};

export default CablePreview;
